import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Typography,
} from "@mui/material";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { useContext, useEffect, useState } from "react";
import { AbilityContext } from "../../../permission/Can";
import { Actions, Subjects } from "../../../permission/constants";

export enum SORT_VALUE {
  CLUB_NAME = "model.team.name",
  SEASON = "model.season.id",
  PURCHASE_VALUE = "purchase_price",
  CURRENT_VALUE = "current_price",
  PROFIT = "profit_price",
  RECENTLY_ADDED = "created_at",
  RECENTLY_CHANGED = "updated_at",
}

export enum SORT_DIRECTION {
  ASC = "asc",
  DESC = "desc",
}
export interface SortElement {
  value: SORT_VALUE;
  direction: SORT_DIRECTION;
}

interface SortButtonProps {
  handleClick: Function;
  targetEntity?: any;
}

const SortButton = ({ handleClick, targetEntity }: SortButtonProps) => {
  const [sortData, setSortData] = useState<SortElement | undefined>(undefined);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<number | undefined>(
    undefined
  );
  const openPopover = Boolean(anchorEl);
  const id = openPopover ? "simple-popover" : undefined;

  const ability = useContext(AbilityContext);

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    handleClick(sortData);
  }, [sortData]);

  const selectedStyle = {
    fontFamily: "Manrope",
    color: "#4663ff !important",
  };

  const nonselectedStyle = {
    fontFamily: "Manrope",
    color: "#3c3c40 !important",
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      }}
    >
      <Paper
        elevation={0}
        component="form"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100px",
        }}
      >
        <Button
          startIcon={<SwapVertIcon />}
          type="button"
          fullWidth
          sx={{
            padding: "10px 0px",
            textTransform: "capitalize",
            height: "50px",
          }}
          aria-label="sort"
          onClick={handleOpenPopover}
        >
          <Typography
            sx={{
              fontWeight: "700 !important",
              fontSize: "16px !important",
            }}
          >
            Sort
          </Typography>
        </Button>
      </Paper>
      <Popover
        id={id}
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          borderRadius: "5px" /* , marginTop: "50px", marginLeft: "-15px" */,
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setSortData({
                  value: SORT_VALUE.CLUB_NAME,
                  direction: SORT_DIRECTION.ASC,
                });
                setSelectedOrder(0);
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    sx={selectedOrder === 0 ? selectedStyle : nonselectedStyle}
                  >
                    Clubname (A-Z)
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setSortData({
                  value: SORT_VALUE.CLUB_NAME,
                  direction: SORT_DIRECTION.DESC,
                });
                setSelectedOrder(1);
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    sx={selectedOrder === 1 ? selectedStyle : nonselectedStyle}
                  >
                    Clubname (Z-A)
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setSortData({
                  value: SORT_VALUE.SEASON,
                  direction: SORT_DIRECTION.DESC,
                });
                setSelectedOrder(2);
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    sx={selectedOrder === 2 ? selectedStyle : nonselectedStyle}
                  >
                    Season (high-low)
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setSortData({
                  value: SORT_VALUE.SEASON,
                  direction: SORT_DIRECTION.ASC,
                });
                setSelectedOrder(3);
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    sx={selectedOrder === 3 ? selectedStyle : nonselectedStyle}
                  >
                    Season (low-high)
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          {targetEntity &&
            ability.can(Actions.VIEW_PURCHASING_VALUE, targetEntity) && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setSortData({
                      value: SORT_VALUE.PURCHASE_VALUE,
                      direction: SORT_DIRECTION.DESC,
                    });
                    setSelectedOrder(4);
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={
                          selectedOrder === 4 ? selectedStyle : nonselectedStyle
                        }
                      >
                        Purchase value (high-low)
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
          {targetEntity &&
            ability.can(Actions.VIEW_PURCHASING_VALUE, targetEntity) && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setSortData({
                      value: SORT_VALUE.PURCHASE_VALUE,
                      direction: SORT_DIRECTION.ASC,
                    });
                    setSelectedOrder(5);
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={
                          selectedOrder === 5 ? selectedStyle : nonselectedStyle
                        }
                      >
                        Purchase value (low-high)
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
          {targetEntity &&
            ability.can(Actions.VIEW_CURRENT_VALUE, targetEntity) && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setSortData({
                      value: SORT_VALUE.CURRENT_VALUE,
                      direction: SORT_DIRECTION.DESC,
                    });
                    setSelectedOrder(6);
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={
                          selectedOrder === 6 ? selectedStyle : nonselectedStyle
                        }
                      >
                        Current value (high-low)
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
          {targetEntity &&
            ability.can(Actions.VIEW_CURRENT_VALUE, targetEntity) && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setSortData({
                      value: SORT_VALUE.CURRENT_VALUE,
                      direction: SORT_DIRECTION.ASC,
                    });
                    setSelectedOrder(7);
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={
                          selectedOrder === 7 ? selectedStyle : nonselectedStyle
                        }
                      >
                        Current value (low-high)
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
          {targetEntity &&
            ability.can(Actions.VIEW_VIRTUAL_PROFIT, targetEntity) && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setSortData({
                      value: SORT_VALUE.PROFIT,
                      direction: SORT_DIRECTION.DESC,
                    });
                    setSelectedOrder(8);
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={
                          selectedOrder === 8 ? selectedStyle : nonselectedStyle
                        }
                      >
                        Profit (high-low)
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
          {targetEntity &&
            ability.can(Actions.VIEW_VIRTUAL_PROFIT, targetEntity) && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setSortData({
                      value: SORT_VALUE.PROFIT,
                      direction: SORT_DIRECTION.ASC,
                    });
                    setSelectedOrder(9);
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={
                          selectedOrder === 9 ? selectedStyle : nonselectedStyle
                        }
                      >
                        Profit (low-high)
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setSortData({
                  value: SORT_VALUE.RECENTLY_ADDED,
                  direction: SORT_DIRECTION.DESC,
                });
                setSelectedOrder(10);
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    sx={selectedOrder === 10 ? selectedStyle : nonselectedStyle}
                  >
                    Recently added
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setSortData({
                  value: SORT_VALUE.RECENTLY_CHANGED,
                  direction: SORT_DIRECTION.DESC,
                });
                setSelectedOrder(11);
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    sx={selectedOrder === 11 ? selectedStyle : nonselectedStyle}
                  >
                    Recently changed
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
    </Box>
  );
};

export default SortButton;
